import { UniqMap } from 'src/lib/UniqMap';

import {
  GENERATION_TOOL,
  GENERATION_TOOL_GROUP,
  type GenerationTool,
  type GenerationToolGroup,
} from './const';
import { isRewriteGenerationTool } from './utils';

export const TOOL_GROUPS = new UniqMap<GenerationToolGroup, GenerationTool[]>();

TOOL_GROUPS.add(GENERATION_TOOL_GROUP.ads, [
  GENERATION_TOOL.googleAppAd,
  GENERATION_TOOL.googleDemandGenAd,
  GENERATION_TOOL.googleDisplayAd,
  GENERATION_TOOL.googlePerformanceMaxAd,
  GENERATION_TOOL.googleRSA,
  GENERATION_TOOL.googleShoppingAd,
  GENERATION_TOOL.googleVideoAd,
  GENERATION_TOOL.linkedinMixtureAd,
  GENERATION_TOOL.linkedinDocumentAd,
  GENERATION_TOOL.linkedinVideoAd,
  GENERATION_TOOL.linkedinTextAd,
  GENERATION_TOOL.linkedinMessageAd,
  GENERATION_TOOL.linkedinConversationAd,
  GENERATION_TOOL.linkedinLeadGenFormAd,
  GENERATION_TOOL.facebookMixtureAd,
  GENERATION_TOOL.outbrainAd,
  GENERATION_TOOL.pinterestMixtureAd,
  GENERATION_TOOL.taboolaAd,
  GENERATION_TOOL.twitterMixtureAd,

  GENERATION_TOOL.facebookHeadlineProductRewrite,
  GENERATION_TOOL.facebookPrimaryTextProductRewrite,
  GENERATION_TOOL.googleHeadlineProductRewrite,
  GENERATION_TOOL.googleDescriptionProductRewrite,
  GENERATION_TOOL.pinterestDescriptionProductRewrite,
  GENERATION_TOOL.taboolaHeadlineProductRewrite,
  GENERATION_TOOL.linkedinIntroSingleImageRewrite,
  GENERATION_TOOL.linkedinHeadlineSingleImageRewrite,
  GENERATION_TOOL.twitterCopyProductRewrite,
]);
TOOL_GROUPS.add(GENERATION_TOOL_GROUP.landingPage, [
  GENERATION_TOOL.landingPageProductAnnouncementMultiPrompt,
  GENERATION_TOOL.landingHeadline,
  GENERATION_TOOL.landingHeadlineRewrite,
  GENERATION_TOOL.landingSubheader,
  GENERATION_TOOL.landingSubheaderRewrite,
  GENERATION_TOOL.landingCTA,
  GENERATION_TOOL.landingBenefits,
  GENERATION_TOOL.landingFeatures,
  GENERATION_TOOL.landingSEOMetadata,
  GENERATION_TOOL.landingSEOMetadataRewrite,
]);
TOOL_GROUPS.add(GENERATION_TOOL_GROUP.campaigns, [
  GENERATION_TOOL.productLaunchMultiPrompt,
  GENERATION_TOOL.emailDripMultiPrompt,
]);
TOOL_GROUPS.add(GENERATION_TOOL_GROUP.social, [
  GENERATION_TOOL.instagramCaption,
  GENERATION_TOOL.instagramCaptionRewrite,
  GENERATION_TOOL.facebookPost,
  GENERATION_TOOL.facebookPostRewrite,
  GENERATION_TOOL.tweet,
  GENERATION_TOOL.tweetRewrite,
  GENERATION_TOOL.linkedinPost,
  GENERATION_TOOL.linkedinPostRewrite,
  GENERATION_TOOL.pinterestMixturePost,
  GENERATION_TOOL.pinterestPinDescriptionRewrite,
  GENERATION_TOOL.pinterestPinTitleRewrite,
  GENERATION_TOOL.youtubeMixturePost,
]);
TOOL_GROUPS.add(GENERATION_TOOL_GROUP.email, [
  GENERATION_TOOL.emailMixture,
  GENERATION_TOOL.emailSubjectRewrite,
  GENERATION_TOOL.emailDripMultiPrompt,
  GENERATION_TOOL.emailSubject,
  GENERATION_TOOL.emailPromotionalBodyRewrite,
  GENERATION_TOOL.emailColdOutreachBodyRewrite,
]);

TOOL_GROUPS.add(GENERATION_TOOL_GROUP.generalWriting, [
  GENERATION_TOOL.basicAida,
  GENERATION_TOOL.basicPas,
  GENERATION_TOOL.basicEli5,
  GENERATION_TOOL.basicSentenceExpander,
  GENERATION_TOOL.basicTalkingPointsToParagraph,
  GENERATION_TOOL.summarizer,
]);

TOOL_GROUPS.add(GENERATION_TOOL_GROUP.ecommerce, [
  GENERATION_TOOL.amazonProductListing,
  GENERATION_TOOL.walmartProductListing,
  GENERATION_TOOL.generalProductListing,
  GENERATION_TOOL.basicProductReview,
  GENERATION_TOOL.basicPerfectHeadline,
  GENERATION_TOOL.smsPromotional,
  GENERATION_TOOL.smsPromotionalRewrite,
]);

TOOL_GROUPS.add(GENERATION_TOOL_GROUP.articleBlog, [
  GENERATION_TOOL.blogDescription,
  GENERATION_TOOL.articleBlogHeadline,
  GENERATION_TOOL.basicBlogIdeas,
]);

TOOL_GROUPS.add(GENERATION_TOOL_GROUP.anyCopy, [GENERATION_TOOL.freestyle]);
TOOL_GROUPS.add(GENERATION_TOOL_GROUP.imageOld, [GENERATION_TOOL.imageWithDescription]);
TOOL_GROUPS.add(GENERATION_TOOL_GROUP.images, [
  GENERATION_TOOL.imageByStyle,
  GENERATION_TOOL.imageImprove,
]);

TOOL_GROUPS.add(GENERATION_TOOL_GROUP.boost, [GENERATION_TOOL.basicContentImprover]);
TOOL_GROUPS.add(GENERATION_TOOL_GROUP.customMultiPrompt, [GENERATION_TOOL.customMultiPrompt]);

export const getToolsByGroup = (toolGroup: GenerationToolGroup): GenerationTool[] =>
  TOOL_GROUPS.get(toolGroup) || [];

export const getSuggestToolsByGroup = (toolGroup: GenerationToolGroup) =>
  getToolsByGroup(toolGroup).filter((tool) => !isRewriteGenerationTool(tool));

export const getGroupByTool = (tool: GenerationTool): GenerationToolGroup | undefined =>
  TOOL_GROUPS.getKeys().find((group) => getToolsByGroup(group).includes(tool));

export const getTeadsTools = (): GenerationTool[] => [
  GENERATION_TOOL.teadsSingleVideoHeadline,
  GENERATION_TOOL.teadsSingleVideoDescription,
];

export const isGenerationToolInSocialGroup = (generationTool: GenerationTool): boolean => {
  const toolGroup = getGroupByTool(generationTool);
  return toolGroup === GENERATION_TOOL_GROUP.social;
};

export const isGenerationToolInProductListingGroup = (generationTool: GenerationTool): boolean => {
  const toolGroup = getGroupByTool(generationTool);
  return toolGroup === GENERATION_TOOL_GROUP.ecommerce;
};

export const isGenerationToolInAdsGroup = (generationTool: GenerationTool): boolean => {
  const toolGroup = getGroupByTool(generationTool);
  return toolGroup === GENERATION_TOOL_GROUP.ads;
};

export const getActiveTemplatesArray = (): GenerationTool[] =>
  TOOL_GROUPS.getAll().reduce((acc, item) => [...acc, ...item[1]], [] as GenerationTool[]);

export const isUsedTemplate = (template: GenerationTool): boolean => {
  const usedTemplatesArray = getActiveTemplatesArray();
  return usedTemplatesArray.includes(template, 0);
};
